/* eslint-disable react/no-unescaped-entities, react/jsx-key */
import { motion } from "framer-motion";
import { X, Check, ExternalLink } from "lucide-react";

const rows = [
  { criterion: "Visibilité sur Google", without: "Invisible", with: "Présent sur la 1ʳᵉ page" },
  { criterion: "Confiance des clients", without: "Faible", with: "Image sérieuse et rassurante" },
  { criterion: "Nouveaux clients", without: "Bouche-à-oreille uniquement", with: "Demandes reçues 24 h/24" },
  { criterion: "Prise de rendez-vous", without: "Téléphone aux heures d'ouverture", with: "Réservation en ligne automatique" },
  { criterion: "Image de marque", without: "Inexistante en ligne", with: "Cohérente et professionnelle" },
  { criterion: "Avis clients & confiance", without: "Difficiles à valoriser", with: "Témoignages mis en avant" },
  { criterion: "Développement possible", without: "Limité", with: "Visibilité multipliée" },
];

export const Comparison = () => (
  <section id="comparatif" data-testid="comparison-section" className="py-24 md:py-32 bg-paper relative overflow-hidden">
    <div
      className="absolute inset-0 -z-0 opacity-50 pointer-events-none"
      style={{ background: "radial-gradient(60% 60% at 50% 0%, rgba(6,182,212,0.06), transparent 70%)" }}
      aria-hidden
    />
    <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
      <div className="mb-16 max-w-3xl">
        <p className="font-mono-label text-cyan-600 flex items-center"><span className="rule bg-cyan" />Comparatif</p>
        <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-ink tracking-tight leading-[1.05]">
          Avec WebUP <em className="italic font-light">vs</em> sans présence digitale.
        </h2>
        <p className="mt-6 text-ink/70 leading-relaxed text-lg">
          La différence entre une entreprise visible et une entreprise oubliée.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
        <div className="bg-paper-soft p-6 lg:p-8 hidden lg:block">
          <p className="font-mono-label text-ink/55">Critère</p>
        </div>
        <div className="bg-white p-6 lg:p-8 border-l-2 border-rose-300/50" data-testid="comp-header-without">
          <p className="font-mono-label text-rose-500">Sans présence digitale</p>
          <p className="mt-2 font-display text-2xl text-ink/70">L'entreprise invisible</p>
        </div>
        <div className="bg-gradient-to-br from-ink to-ink-600 text-white p-6 lg:p-8 relative overflow-hidden" data-testid="comp-header-with">
          <div className="absolute -top-12 -right-12 w-44 h-44 bg-cyan/30 rounded-full blur-3xl" aria-hidden />
          <p className="font-mono-label text-cyan-300 relative">Avec WebUP</p>
          <p className="mt-2 font-display text-2xl relative">Un site qui inspire confiance et donne envie de vous contacter</p>
        </div>

        {rows.map((r, i) => (
          <motion.div
            key={r.criterion}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.03 }}
            className="contents"
            data-testid={`comparison-row-${i}`}
          >
            <div className="bg-paper-soft p-5 lg:p-6 flex items-center">
              <p className="text-ink/85 text-sm font-medium">{r.criterion}</p>
            </div>
            <div className="bg-white p-5 lg:p-6 flex items-center gap-3">
              <span className="h-7 w-7 rounded-full bg-rose-50 inline-flex items-center justify-center text-rose-500 shrink-0">
                <X size={14} strokeWidth={3} />
              </span>
              <p className="text-ink/65 text-sm">{r.without}</p>
            </div>
            <div className="bg-white p-5 lg:p-6 flex items-center gap-3 border-l-2 border-cyan/40">
              <span className="h-7 w-7 rounded-full bg-cyan/15 inline-flex items-center justify-center text-cyan-600 shrink-0">
                <Check size={14} strokeWidth={3} />
              </span>
              <p className="text-ink text-sm font-medium">{r.with}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-6 bg-white border border-ink/10 p-6 md:p-8 lift">
        <p className="text-ink/75 text-sm md:text-base">
          <strong className="text-ink">88 %</strong> des clients vérifient une entreprise sur internet avant
          de la contacter. Sans site, ils partent chez le concurrent.
        </p>
        <a
          href="#contact"
          className="inline-flex items-center gap-3 h-12 px-6 btn-premium text-sm font-medium shrink-0"
          data-testid="comparison-cta"
        >
          Prendre rendez-vous →
        </a>
      </div>
      <p className="mt-3 text-xs text-ink/45 flex items-center gap-1.5">
        <ExternalLink size={11} /> Sources : BrightLocal Local Consumer Review Survey 2024, HubSpot State of Marketing 2024.
      </p>
    </div>
  </section>
);
